import React from 'react';
import api from 'api';
import types from '../types';
import {LoginPayload} from 'types/auth';
import {useDispatch} from 'react-redux';
import {dispatchAsync} from 'redux/common';
import {currentLocationIsLogin} from 'common/utils';
import {ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import ApiService from 'api/ApiService';
import history from 'redux/history';
import useIsMountedRef from 'common/hooks/useIsMountedRef';
import {useOpenSubDialog} from 'features/layout/redux/hooks/useOpenSubDialog';
import {DialogTypes} from 'common/names';
import {useTranslation} from 'react-i18next';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {get} from 'lodash';
import {layoutActions} from 'features/layout/redux';
import {refreshCsrfOnce} from 'api/fetchCsrfToken';

export const useConnect = () => {
   const isMountedRef = useIsMountedRef();
   const {t} = useTranslation();

   const showAlertMaxConnections = useOpenSubDialog(
      DialogTypes.AlertDialog.id,
      null,
      t('Connect Failure'),
      t(
         'The Diplomat server license does not support sufficient connections\n of the proper type to honor this connection attempt.'
      )
   );

   const [expiredData, setExpired] = React.useState<any>(null);
   const dispatch = useDispatch();
   const cb = React.useCallback(
      async (
         values: LoginPayload,
         {setSubmitting, setErrors}: FormOnSubmitHelpers<LoginPayload>
      ) => {
         try {
            await refreshCsrfOnce();
         } catch (e) {
            console.error(e);
         }

         return dispatch(
            dispatchAsync(api.connect, types.LOGIN, {
               getMethod: ApiService.postSecureJson,
               data: {
                  ...values,
                  overrideDisabled: true,
               },
               onSuccess: (res: any, dispatch?: ThunkDispatch<{}, {}, AnyAction>) => {
                  // await dispatch(action(types.LOGIN.SUCCESS, values.userName));
                  if (isMountedRef.current) {
                     setSubmitting(false);
                  }
                  if (currentLocationIsLogin()) {
                     history.push('/');
                  }
               },
               onError: async (e: any, _dispatch, eResponse) => {
                  console.debug(e);
                  if (isMountedRef.current) {
                     let errorHandled = false;
                     if (eResponse?.status === 419) {
                        try {
                           setExpired(values.userName);
                        } catch (e) {
                           console.error(e);
                        }
                     }

                     if (eResponse?.status === 409 && e.maxConnections) {
                        showAlertMaxConnections();
                        errorHandled = true;
                     }

                     if (eResponse?.status === 428) {
                        dispatch(
                           layoutActions.openSubDialog(DialogTypes.AuthenticatorSetupDialog.id)
                        );
                        errorHandled = true;
                     }

                     if (eResponse?.status === 412) {
                        dispatch(
                           layoutActions.openSubDialog(DialogTypes.AuthenticatorVerifyDialog.id)
                        );
                        errorHandled = true;
                     }

                     setSubmitting(false);
                     setErrors({
                        userName: get(e, 'errors.0.message', e),
                     });
                     return errorHandled;
                  }
               },
            })
         );
      },
      [dispatch, isMountedRef, showAlertMaxConnections]
   );

   return [cb, expiredData, setExpired];
};
